/**
 * useCheckpointTimer - Timer management for checkpoint close button visibility
 *
 * Shows close button either:
 * - Immediately when conversation is ended
 * - After a delay (default 10s) for ongoing conversations
 */
import { useState, useEffect, useRef } from "react"
import { TIMING } from "./constants"

/**
 * @param {boolean} isEnded - Whether the conversation has ended
 * @param {boolean} isLoading - Whether the conversation is loading
 * @param {number} delay - Delay before showing close button (ms)
 * @returns {boolean} canShowCloseButton
 */
export const useCheckpointTimer = (
    isEnded,
    isLoading = false,
    delay = TIMING.CLOSE_BUTTON_DELAY
) => {
    const [canShowCloseButton, setCanShowCloseButton] = useState(false)
    const timerRef = useRef(null)

    useEffect(() => {
        // Clear any existing timer
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }

        // If ended and not loading, show immediately
        if (isEnded && !isLoading) {
            setCanShowCloseButton(true)
            return
        }

        // If loading, hide and wait
        if (isLoading) {
            setCanShowCloseButton(false)
            return
        }

        // Otherwise, show after delay
        setCanShowCloseButton(false)
        timerRef.current = setTimeout(() => {
            setCanShowCloseButton(true)
        }, delay)

        // Cleanup on unmount or dependency change
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }
    }, [isEnded, isLoading, delay])

    return canShowCloseButton
}

export default useCheckpointTimer
